import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { StudentStateType, StudentType } from '../../types';
import type { MessageStudentsProps as MessageStudentsType } from '../modules/MessageStudents';
import type { StudentsTableAccessors } from '../../../global/tables/StudentsTable';
import { CollapseSection, StudentsDisplay, ValueHeadline } from '../modules';
import { Button, CompareProgressBar, Loader } from '../../../components';
import { CongratulateButton, StudentsTable } from '../../../global';
import { mapAllGroupReportPosNeg } from '../../../mapper/groupMapper';
import type { SectionProps } from '../Home';
import CluedoMexicanWave from '../../../assets/Cluedo-Nudges-Mexican-Wave.svg';

interface Props extends SectionProps {
  isVisible: boolean;
  /**
   * Confetti container.
   */
  confetti: React.ReactNode;

  /**
   * Handles click on "Got it" button
   * in order to hide section.
   */
  onGotIt(): void;

  /**
   * Handles click on "Message them" button
   * in order to show message modal.
   */
  onMessageStudents(students: MessageStudentsType['students'], type: MessageStudentsType['type']): void;
}

const still_going_column: Record<StudentStateType, StudentsTableAccessors | null> = {
  started: 'maths_age',
  no_logged: 'login_details',
  no_assessment: 'progress',
  no_progression: null,
  in_reassessment: 'progress',
};

const GetYourStudentsStarted = (
  {
    isLoading,
    isVisible,
    groupReport,
    confetti,
    onGotIt,
    onMessageStudents,
    open,
    setOpen,
  }: Props,
): JSX.Element => {
  const [ data, setData ] = useState<{
    positive: StudentType[],
    negative: StudentType[]
  }>({
    positive: [],
    negative: [],
  });

  const { translate } = useI18n();

  useEffect(() => {
    if (null == groupReport) {
      return;
    }

    setData(mapAllGroupReportPosNeg(groupReport));
  }, [ groupReport ]);

  const getFilteredByState = (data: StudentType[], state: StudentStateType) => {
    return data.filter((student) => student.state === state);
  };

  const hasNoStudents = 0 === data.negative.length && 0 === data.positive.length;
  const allStudentsStarted = data.positive.length + data.negative.length === data.positive.length;
  const dataCy = hasNoStudents ? 'home_getting_started_no_students' : `home_getting_started_${isVisible ? 'visible' : 'hidden'}`;

  const negativeStudents = data.negative.filter((student) => student.state !== 'in_reassessment');
  const inReassessmentStudents = data.negative.filter((student) => student.state === 'in_reassessment');
  return (
    <>
      <CollapseSection
        headline={
          isLoading
            ? ''
            : (
              (hasNoStudents)
                ? translate('pages.HOME.GET_STARTED.NO_STUDENTS')
                : translate('pages.HOME.GET_STARTED.HEADLINE')
            )
        }
        strapline={isLoading || hasNoStudents ? '' : translate('pages.HOME.GET_STARTED.STRAPLINE')}
        dataCy={dataCy}
        open={open}
        setOpen={setOpen}
        scrollToTop
      >
        {isLoading
          ? (<Loader />)
          : (
            <>
              {hasNoStudents
                ? (
                  <div className={'text-muted italic space-y-2'}>
                    <p>{translate('pages.HOME.GET_STARTED.NO_STUDENTS_info1')}</p>
                    <p>{translate('pages.HOME.GET_STARTED.NO_STUDENTS_info2')}</p>
                  </div>
                )
                : (
                  <>
                    <CompareProgressBar
                      id={'students'}
                      borderColor={'primary'}
                      total={data.positive.length + data.negative.length}
                      values={{
                        a: {
                          value: data.positive.length,
                          color: 'positive',
                          message: `${data.positive.length}
                      ${' '}${translate('pages.HOME.GET_STARTED.STARTED.DESCRIPTION', { count: data.positive.length })}`,
                        },
                        b: {
                          value: data.negative.length,
                          color: 'negative',
                          message: `${data.negative.length}
                      ${' '}${translate('pages.HOME.GET_STARTED.GOING.DESCRIPTION', { count: data.negative.length })}`,
                        },
                      }}
                      data-cy="getStarted-bar"
                    />
                    {allStudentsStarted
                      ? (
                        <div className={'my-md flex flex-col items-center flex-none space-y-2'}>
                          {confetti}
                          <ValueHeadline
                            value={null}
                            showMoreInfo={false}
                            className={'mb-md'}
                          />
                          <img src={CluedoMexicanWave} />
                          <p>{translate('pages.HOME.GET_STARTED.STARTED.ALL')}</p>
                          <p>{translate('pages.HOME.GET_STARTED.STARTED.HIDE_SECTION')}</p>
                          <Button
                            color={'positive'}
                            isOutline
                            className={'uppercase'}
                            isBold
                            onClick={onGotIt}
                          >
                            {translate('components.BUTTONS.HIDE_THIS')}
                          </Button>
                        </div>
                      )
                      : (
                        <>
                          {/* Positive */}
                          <StudentsDisplay
                            data={data.positive}
                            color={'positive'}
                            buttonText={'see'}
                            unit={`${translate('pages.HOME.GET_STARTED.STARTED.DESCRIPTION', { count: data.positive.length })}`}
                            moreInfo={translate('pages.HOME.GET_STARTED.STARTED.INFO', { count: data.positive.length })}
                            className={'my-lg'}
                            data-cy="getStarted-display-fully"
                          >
                            <div className={'flex justify-end mb-md'}>
                              <CongratulateButton className={'mt-md'} onClick={() => onMessageStudents(data.positive, 'progressions3')} />
                            </div>
                            <StudentsTable
                              id={'students-list_started'}
                              data={data.positive}
                              color={'positive'}
                              data-cy="getStarted-table-fully"
                            />
                          </StudentsDisplay>
                          {/* Negative */}
                          <StudentsDisplay
                            data={negativeStudents}
                            color={'negative'}
                            buttonText={'help'}
                            unit={`${translate('pages.HOME.GET_STARTED.GOING.DESCRIPTION', { count: negativeStudents.length })}`}
                            moreInfo={translate('pages.HOME.GET_STARTED.GOING.INFO', { count: negativeStudents.length })}
                            className={'my-lg'}
                            data-cy="getStarted-display-still-going"
                          >
                            {([ 'no_logged', 'no_assessment', 'no_progression' ] as StudentStateType[]).map((state) => {
                              if (0 === getFilteredByState(negativeStudents, state).length) {
                                return null;
                              }

                              return (
                                <StudentsDisplay
                                  key={`negative_state_${state}`}
                                  data={getFilteredByState(negativeStudents, state)}
                                  unit={`${translate(`pages.HOME.GET_STARTED.${state.toUpperCase()}.DESCRIPTION`, {
                                    count: getFilteredByState(data.negative, state).length,
                                  })}`}
                                  moreInfo={translate(`pages.HOME.GET_STARTED.${state.toUpperCase()}.INFO`)}
                                  color={'negative'}
                                  buttonText={'see'}
                                  disableContainer
                                  className={'mb-lg'}
                                  data-cy={`getStarted-display-${state}`}
                                >
                                  <StudentsTable
                                    id={`students-list_${state}`}
                                    data={getFilteredByState(negativeStudents, state)}
                                    column={still_going_column[state]}
                                    color={'negative'}
                                    withBorder
                                    headerMessage={translate(`pages.HOME.GET_STARTED.${state.toUpperCase()}.HELP`)}
                                    className={'container'}
                                    data-cy={`getStarted-table-${state}`}
                                  />
                                </StudentsDisplay>
                              );
                            })}
                          </StudentsDisplay>
                          {/* In Reassessment */}
                          { inReassessmentStudents.length
                            ? <StudentsDisplay
                              data={inReassessmentStudents}
                              color={'negative'}
                              buttonText={'help'}
                              unit={`${translate('pages.HOME.GET_STARTED.IN_REASSESSMENT.DESCRIPTION', { count: inReassessmentStudents.length })}`}
                              moreInfo={translate('pages.HOME.GET_STARTED.IN_REASSESSMENT.INFO', { count: inReassessmentStudents.length })}
                              className={'my-lg'}
                              data-cy="inReassessment-display-fully"
                            >
                              <StudentsTable
                                id={'students-list_reassessment'}
                                data={inReassessmentStudents}
                                color={'negative'}
                                column={still_going_column.in_reassessment}
                                headerMessage={translate('pages.HOME.GET_STARTED.IN_REASSESSMENT.HELP')}
                                data-cy="inReassessment-table-fully"
                              />
                            </StudentsDisplay>
                            : null }
                        </>
                      )}
                  </>
                )}
            </>
          )}
      </CollapseSection>
    </>
  );
};

GetYourStudentsStarted.displayName = 'GetYourStudentsStarted';

export default GetYourStudentsStarted;
